import { useState, useEffect, useRef, useCallback } from 'react';
import { RealtimeService } from '../services/RealtimeService';

function useVoiceMode({
  enabled,
  role = 'counsellor',
  sessionActive,
  sessionEnded,
  sessionId,
  conversationLength,
  onConversationStart,
  onConversationAppend,
  onAnalytics,
  onClassification,
  onGuidance,
  onGuidanceError,
  onSessionEnded,
  setConversation,
  setLoading,
  notify,
}) {
  const [voiceMode, setVoiceMode] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState('idle');
  const [voiceBetaModalOpen, setVoiceBetaModalOpen] = useState(false);
  const [voiceBetaSeenThisSession, setVoiceBetaSeenThisSession] = useState(false);
  const realtimeRef = useRef(null);

  const disconnectRealtime = useCallback(() => {
    if (realtimeRef.current) {
      realtimeRef.current.disconnect();
      realtimeRef.current = null;
    }
    setVoiceMode(false);
    setVoiceStatus('idle');
  }, []);

  const startVoiceMode = useCallback(async () => {
    if (voiceMode) {
      disconnectRealtime();
      setLoading(false);
      return;
    }
    if (!enabled || !sessionActive || sessionEnded || !sessionId) return;

    disconnectRealtime();
    setVoiceMode(true);

    const userSpeaker = role === 'client' ? 'client' : 'counsellor';
    const botSpeaker = role === 'client' ? 'counsellor' : 'client';
    const svc = new RealtimeService({
      role,
      onTranscript: (text) => {
        if (onConversationStart && conversationLength === 0) {
          onConversationStart();
        }
        onConversationAppend({ speaker: userSpeaker, text });
      },
      onClientResponse: (text) => {
        // Replace the contemplating placeholder appended when processing
        // started; fall back to a plain append if it's already gone.
        setConversation((prev) => {
          const idx = prev.findIndex((m) => m.isStreaming);
          if (idx === -1) return [...prev, { speaker: botSpeaker, text }];
          const updated = [...prev];
          updated[idx] = { speaker: botSpeaker, text };
          return updated;
        });
        setLoading(false);
      },
      onClassification: (labels) => onClassification?.(labels),
      onGuidance: (feedback) => onGuidance?.(feedback),
      onGuidanceError: (message) => onGuidanceError?.(message),
      onAnalytics: (data) => {
        onAnalytics?.(data);
        if (data.session_ended) {
          onSessionEnded?.(data.end_reason || 'The client has ended the session');
          disconnectRealtime();
        }
      },
      onStatusChange: (status) => {
        setVoiceStatus(status);
        setLoading(status === 'processing');
        if (status === 'processing') {
          setConversation((prev) => (
            prev.some((m) => m.isStreaming)
              ? prev
              : [...prev, { speaker: botSpeaker, text: '', isStreaming: true }]
          ));
        } else if (status === 'listening' || status === 'idle') {
          // A turn that never produced a reply (error, discarded overlap,
          // disconnect) must not leave the placeholder behind.
          setConversation((prev) => (
            prev.some((m) => m.isStreaming) ? prev.filter((m) => !m.isStreaming) : prev
          ));
        }
      },
      onError: (err) => {
        console.error('Realtime error:', err);
        notify(err.message || 'Voice mode error. Please restart the session.');
        setLoading(false);
        disconnectRealtime();
      },
    });

    realtimeRef.current = svc;

    try {
      await svc.connect();
    } catch (err) {
      console.error('Failed to connect voice mode:', err);
      disconnectRealtime();
      notify('Failed to start voice mode. Please try again.');
    }
  }, [
    conversationLength,
    disconnectRealtime,
    enabled,
    notify,
    onAnalytics,
    onClassification,
    onConversationAppend,
    onConversationStart,
    onGuidance,
    onGuidanceError,
    onSessionEnded,
    role,
    sessionActive,
    sessionEnded,
    sessionId,
    setConversation,
    setLoading,
    voiceMode,
  ]);

  const handleEnterVoiceMode = useCallback(async () => {
    if (!voiceMode && !voiceBetaSeenThisSession) {
      setVoiceBetaSeenThisSession(true);
      setVoiceBetaModalOpen(true);
      return;
    }
    await startVoiceMode();
  }, [startVoiceMode, voiceBetaSeenThisSession, voiceMode]);

  const handleBeginVoiceMode = useCallback(async () => {
    setVoiceBetaModalOpen(false);
    await startVoiceMode();
  }, [startVoiceMode]);

  const closeVoiceBetaModal = useCallback(() => {
    setVoiceBetaModalOpen(false);
  }, []);

  const resetVoiceModeUi = useCallback(() => {
    setVoiceBetaModalOpen(false);
    setVoiceBetaSeenThisSession(false);
  }, []);

  useEffect(() => {
    if (!sessionActive || !sessionId || sessionEnded) {
      setVoiceBetaSeenThisSession(false);
    }
  }, [sessionActive, sessionEnded, sessionId]);

  useEffect(() => {
    return () => {
      disconnectRealtime();
    };
  }, [disconnectRealtime]);

  useEffect(() => {
    if (!enabled || !sessionActive || sessionEnded) {
      disconnectRealtime();
    }
  }, [disconnectRealtime, enabled, sessionActive, sessionEnded]);

  return {
    voiceMode,
    voiceStatus,
    voiceBetaModalOpen,
    handleEnterVoiceMode,
    handleBeginVoiceMode,
    closeVoiceBetaModal,
    resetVoiceModeUi,
    disconnectRealtime,
  };
}

export default useVoiceMode;
